import React from 'react';
import { useTranslation } from 'react-i18next';

interface PasswordStrengthProps {
  password: string;
}

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({ password }) => {
  const { t } = useTranslation();

  const calculateStrength = (password: string): number => {
    let score = 0;

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character type checks
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    return score;
  };

  // Función para verificar si la contraseña cumple con TODOS los requisitos
  const isPasswordComplete = (password: string): boolean => {
    return (
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password)
    );
  };

  const getStrengthLabel = (score: number, isComplete: boolean): string => {
    if (!isComplete) return t('pages.users.fields.passwordStrength.incomplete');
    if (score <= 2) return t('pages.users.fields.passwordStrength.weak');
    if (score <= 3) return t('pages.users.fields.passwordStrength.medium');
    if (score <= 5) return t('pages.users.fields.passwordStrength.strong');
    return t('pages.users.fields.passwordStrength.veryStrong');
  };

  const getStrengthColor = (score: number, isComplete: boolean): string => {
    if (!isComplete) return 'bg-red-500';
    if (score <= 2) return 'bg-red-500';
    if (score <= 3) return 'bg-yellow-500';
    if (score <= 5) return 'bg-green-500';
    return 'bg-emerald-500';
  };

  const strength = calculateStrength(password);
  const isComplete = isPasswordComplete(password);
  const strengthPercentage = isComplete ? (strength / 6) * 100 : 0;
  const strengthLabel = getStrengthLabel(strength, isComplete);
  const strengthColor = getStrengthColor(strength, isComplete);

  return (
    <div className="mt-1">
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${strengthColor} transition-all duration-300`}
          style={{ width: `${strengthPercentage}%` }}
        />
      </div>
      <div className="mt-1 text-sm text-gray-600">{strengthLabel}</div>
      <ul className="mt-2 text-xs text-gray-500 space-y-1">
        <li className={password.length >= 8 ? 'text-green-600' : 'text-red-600'}>
          {t('pages.users.fields.passwordRequirements.length')}
        </li>
        <li className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-red-600'}>
          {t('pages.users.fields.passwordRequirements.uppercase')}
        </li>
        <li className={/[a-z]/.test(password) ? 'text-green-600' : 'text-red-600'}>
          {t('pages.users.fields.passwordRequirements.lowercase')}
        </li>
        <li className={/[0-9]/.test(password) ? 'text-green-600' : 'text-red-600'}>
          {t('pages.users.fields.passwordRequirements.number')}
        </li>
        <li className={/[^A-Za-z0-9]/.test(password) ? 'text-green-600' : 'text-red-600'}>
          {t('pages.users.fields.passwordRequirements.special')}
        </li>
      </ul>
    </div>
  );
};
